/**
 * AIGenome - 遺伝的アルゴリズム用パラメータ定義
 *
 * hardAIの判断ロジックをパラメータ化したもの。
 * 各パラメータは遺伝的アルゴリズムで最適化される。
 */

import {
  type EvaluationWeights,
  type MagicEvaluationConfig,
  DEFAULT_WEIGHTS,
  DEFAULT_MAGIC_CONFIG,
  EVALUATION_PARAM_RANGES,
  MAGIC_CONFIG_RANGES,
} from '../evaluation';

// =============================================================================
// パラメータ型定義
// =============================================================================

/** 攻撃ターゲット評価パラメータ */
export interface AttackScoringParams {
  /** 倒せる相手へのボーナス (0-2000) */
  killBonus: number;
  /** 脅威度(ATK)の重み (0.0-2.0) */
  threatWeight: number;
  /** 与ダメージの重み (0.0-2.0) */
  damageWeight: number;
  /** 飛行安全攻撃ボーナス (0-1000) */
  flightSafeBonus: number;
  /** 反撃死ペナルティ (0-1500) */
  counterDeathPenalty: number;
  /** 地上ターゲット優先ボーナス (0-500) */
  groundTargetBonus: number;
  /** 直接攻撃選択閾値 (0.0-1.0) */
  directAttackThreshold: number;
}

/** 召喚判断パラメータ */
export interface SummonPreferenceParams {
  /** コスト効率重視度 (0.0-2.0) */
  costEfficiencyWeight: number;
  /** 純攻撃力重視度 (0.0-2.0) */
  rawAttackWeight: number;
  /** 不利時の積極性 (0.0-1.0) */
  disadvantageAggression: number;
}

/** スキル使用パラメータ */
export interface SkillUsageParams {
  /** 基本技使用率 (0.0-1.0) */
  basicSkillRate: number;
  /** 上級技優先度 (0.0-1.0) */
  advancedSkillPriority: number;
}

/** 行動確率パラメータ */
export interface ActionProbabilityParams {
  /** トリガー発動率 (0.0-1.0) */
  triggerActivationRate: number;
  /** 優先魔法使用率 (0.0-1.0) */
  magicPriorityRate: number;
  /** その他魔法使用率 (0.0-1.0) */
  magicOtherRate: number;
}

/** フィールド/フェイズカード配置パラメータ */
export interface CardPlacementParams {
  /** 序盤配置ボーナス (0-100) */
  earlyGameBonus: number;
  /** 手札シナジー重み (0-50) */
  synergyHandWeight: number;
  /** フィールドシナジー重み (0-50) */
  synergyFieldWeight: number;
  /** フィールドカード配置スコア閾値 (0-100) */
  fieldCardThreshold: number;
  /** フェイズカード配置スコア閾値 (0-100) */
  phaseCardThreshold: number;
}

/** チャージ戦略パラメータ */
export interface ChargeStrategyParams {
  /** 上級技準備優先度 (0.0-1.0) */
  prioritizeAdvancedReady: number;
  /** 高コストモンスター温存度 (0.0-1.0) */
  preserveHighCostMonsters: number;
}

/** 深海領域戦略パラメータ */
export interface DeepSeaStrategyParams {
  /** 潜航を行う最小SP不足量（0-5）: SPがこの値以上不足したら潜航 */
  submergeMinSPDeficit: number;
  /** フィールド満杯時の潜航優先度 (0.0-1.0) */
  submergeWhenFieldFull: number;
  /** 浮上する最小深度 (1-5): この深度以上で浮上を検討 */
  surfaceMinDepth: number;
  /** 浮上効果発動優先深度 (3-7): この深度以上なら最優先で浮上 */
  surfaceEffectDepth: number;
  /** フィールド空時の即浮上閾値 (0-3): フィールドにモンスターがいない時、この深度以上で浮上 */
  surfaceWhenEmptyField: number;
}

/** AIゲノム（全パラメータを含む） */
export interface AIGenome {
  attackScoring: AttackScoringParams;
  summonPreference: SummonPreferenceParams;
  skillUsage: SkillUsageParams;
  actionProbabilities: ActionProbabilityParams;
  cardPlacement: CardPlacementParams;
  chargeStrategy: ChargeStrategyParams;
  /** 深海領域戦略パラメータ */
  deepSeaStrategy: DeepSeaStrategyParams;
  /** 盤面評価パラメータ（シミュレーション評価用） */
  boardEvaluation: EvaluationWeights;
  /** 魔法評価パラメータ（シミュレーション評価用） */
  magicEvaluation: MagicEvaluationConfig;
}

// =============================================================================
// パラメータ範囲定義
// =============================================================================

export interface ParamRange {
  min: number;
  max: number;
}

export const PARAM_RANGES: Record<string, Record<string, ParamRange>> = {
  attackScoring: {
    killBonus: { min: 0, max: 2000 },
    threatWeight: { min: 0, max: 2.0 },
    damageWeight: { min: 0, max: 2.0 },
    flightSafeBonus: { min: 0, max: 1000 },
    counterDeathPenalty: { min: 0, max: 1500 },
    groundTargetBonus: { min: 0, max: 500 },
    directAttackThreshold: { min: 0, max: 1.0 },
  },
  summonPreference: {
    costEfficiencyWeight: { min: 0, max: 2.0 },
    rawAttackWeight: { min: 0, max: 2.0 },
    disadvantageAggression: { min: 0, max: 1.0 },
  },
  skillUsage: {
    basicSkillRate: { min: 0, max: 1.0 },
    advancedSkillPriority: { min: 0, max: 1.0 },
  },
  actionProbabilities: {
    triggerActivationRate: { min: 0, max: 1.0 },
    magicPriorityRate: { min: 0, max: 1.0 },
    magicOtherRate: { min: 0, max: 1.0 },
  },
  cardPlacement: {
    earlyGameBonus: { min: 0, max: 100 },
    synergyHandWeight: { min: 0, max: 50 },
    synergyFieldWeight: { min: 0, max: 50 },
    fieldCardThreshold: { min: 0, max: 100 },
    phaseCardThreshold: { min: 0, max: 100 },
  },
  chargeStrategy: {
    prioritizeAdvancedReady: { min: 0, max: 1.0 },
    preserveHighCostMonsters: { min: 0, max: 1.0 },
  },
  deepSeaStrategy: {
    submergeMinSPDeficit: { min: 0, max: 5 },
    submergeWhenFieldFull: { min: 0, max: 1.0 },
    surfaceMinDepth: { min: 1, max: 5 },
    surfaceEffectDepth: { min: 3, max: 7 },
    surfaceWhenEmptyField: { min: 0, max: 3 },
  },
  // 盤面評価パラメータ（シミュレーション評価用）
  boardEvaluation: EVALUATION_PARAM_RANGES as unknown as Record<string, ParamRange>,
  // 魔法評価パラメータ（シミュレーション評価用）
  magicEvaluation: MAGIC_CONFIG_RANGES as unknown as Record<string, ParamRange>,
};

// =============================================================================
// デフォルト値（hardAI相当）
// =============================================================================

export const DEFAULT_GENOME: AIGenome = {
  attackScoring: {
    killBonus: 1000,
    threatWeight: 1.0,
    damageWeight: 1.0,
    flightSafeBonus: 500,
    counterDeathPenalty: 800,
    groundTargetBonus: 200,
    directAttackThreshold: 0.3,
  },
  summonPreference: {
    costEfficiencyWeight: 1.0,
    rawAttackWeight: 0.5,
    disadvantageAggression: 1.0,
  },
  skillUsage: {
    basicSkillRate: 0.6,
    advancedSkillPriority: 1.0,
  },
  actionProbabilities: {
    triggerActivationRate: 0.8,
    magicPriorityRate: 1.0,
    magicOtherRate: 0.5,
  },
  cardPlacement: {
    earlyGameBonus: 30,
    synergyHandWeight: 20,
    synergyFieldWeight: 25,
    fieldCardThreshold: 40,
    phaseCardThreshold: 30,
  },
  chargeStrategy: {
    prioritizeAdvancedReady: 1.0,
    preserveHighCostMonsters: 0.8,
  },
  deepSeaStrategy: {
    submergeMinSPDeficit: 1,      // SP1以上不足で潜航検討
    submergeWhenFieldFull: 0.8,   // フィールド満杯時80%潜航
    surfaceMinDepth: 3,           // 深度3以上で浮上検討
    surfaceEffectDepth: 5,        // 深度5以上で最優先浮上
    surfaceWhenEmptyField: 1,     // フィールド空なら深度1でも浮上
  },
  // 盤面評価パラメータ（シミュレーション評価用）
  boardEvaluation: { ...DEFAULT_WEIGHTS },
  // 魔法評価パラメータ（シミュレーション評価用）
  magicEvaluation: { ...DEFAULT_MAGIC_CONFIG },
};

// =============================================================================
// 進化型AI（GA訓練済みパラメータ）
// Stage 1: 閾値最適化（30個体, 50世代, 3デッキ, fitness: 992.78）
// Stage 2: シミュレーション評価最適化 vs hardAI（50個体, 100世代, 3デッキ, fitness: 1534.81）
// =============================================================================

export const EVOLVED_GENOME: AIGenome = {
  // === 閾値パラメータ（Stage 1 GA最適化済み）===
  attackScoring: {
    killBonus: 1420.93,
    threatWeight: 0.472,
    damageWeight: 0.561,
    flightSafeBonus: 353.09,
    counterDeathPenalty: 0,
    groundTargetBonus: 146.94,
    directAttackThreshold: 0.43,
  },
  summonPreference: {
    costEfficiencyWeight: 0,
    rawAttackWeight: 1.308,
    disadvantageAggression: 0.737,
  },
  skillUsage: {
    basicSkillRate: 1.0,
    advancedSkillPriority: 0.713,
  },
  actionProbabilities: {
    triggerActivationRate: 0.12,
    magicPriorityRate: 0.445,
    magicOtherRate: 0.038,
  },
  cardPlacement: {
    earlyGameBonus: 18.17,
    synergyHandWeight: 8.60,
    synergyFieldWeight: 13.90,
    fieldCardThreshold: 39.0,
    phaseCardThreshold: 24.59,
  },
  chargeStrategy: {
    prioritizeAdvancedReady: 0.434,
    preserveHighCostMonsters: 0.805,
  },
  // === 深海領域戦略パラメータ（Stage 2 GA訓練済み + 深度3基準に調整）===
  deepSeaStrategy: {
    submergeMinSPDeficit: 2.60,       // SP2.6以上不足で潜航検討
    submergeWhenFieldFull: 0.09,      // フィールド満杯時9%潜航（控えめ）
    surfaceMinDepth: 3,               // 深度3以上で浮上検討（召喚酔い回避）
    surfaceEffectDepth: 5,            // 深度5以上で最優先浮上（浮上効果発動）
    surfaceWhenEmptyField: 3,         // フィールド空でも深度3以上で浮上（召喚酔い回避）
  },
  // === シミュレーション評価パラメータ（Stage 2 vs hardAI 最適化済み）===
  boardEvaluation: {
    myLifeWeight: 1.3997903410862047,
    oppLifeWeight: 0.6444646656730523,
    myMonsterCountWeight: 179.21233778056683,
    myTotalAttackWeight: 1.618415111099821,
    myTotalHpWeight: 0.32201452706776,
    myFlightCountWeight: 133.60437453453966,
    myAntiAirCountWeight: 129.2915455400513,
    myGuardCountWeight: 42.43603433591285,
    myBuffCountWeight: 22.027793143590195,
    myDebuffCountWeight: -75.65559996450217,
    oppDebuffCountWeight: 63.967491774390275,
    myPlayerDebuffWeight: -196.26893332683488,
    oppPlayerDebuffWeight: 200,
    oppMonsterCountWeight: -144.5044987218723,
    oppTotalAttackWeight: -0.617515711599802,
    deepSeaCardWeight: 8.456940440489738,
    deepSeaDepthWeight: 66.79463701077518,
    activeSPWeight: 37.92184690030292,
    restedSPWeight: 8.315130522504603,
    maxSPWeight: 80.33893486271143,
    handSizeWeight: 53.26189147065774,
    costReductionWeight: 32.49286870182014,
    graveyardSizeWeight: 11.051171001389438,
    deckSizeWeight: 1.7229288808242345,
  },
  magicEvaluation: {
    spCostPenalty: 42.145945478394665,
    minValueThreshold: 16.051953910781105,
  },
};

// =============================================================================
// ユーティリティ関数
// =============================================================================

/**
 * ランダムな深海領域戦略パラメータを生成
 */
export function createRandomDeepSeaStrategy(): DeepSeaStrategyParams {
  const ranges = PARAM_RANGES.deepSeaStrategy;
  return {
    submergeMinSPDeficit: ranges.submergeMinSPDeficit.min +
      Math.random() * (ranges.submergeMinSPDeficit.max - ranges.submergeMinSPDeficit.min),
    submergeWhenFieldFull: ranges.submergeWhenFieldFull.min +
      Math.random() * (ranges.submergeWhenFieldFull.max - ranges.submergeWhenFieldFull.min),
    surfaceMinDepth: ranges.surfaceMinDepth.min +
      Math.random() * (ranges.surfaceMinDepth.max - ranges.surfaceMinDepth.min),
    surfaceEffectDepth: ranges.surfaceEffectDepth.min +
      Math.random() * (ranges.surfaceEffectDepth.max - ranges.surfaceEffectDepth.min),
    surfaceWhenEmptyField: ranges.surfaceWhenEmptyField.min +
      Math.random() * (ranges.surfaceWhenEmptyField.max - ranges.surfaceWhenEmptyField.min),
  };
}

/**
 * ランダムなゲノムを生成
 */
export function createRandomGenome(): AIGenome {
  const genome: AIGenome = {
    attackScoring: {} as AttackScoringParams,
    summonPreference: {} as SummonPreferenceParams,
    skillUsage: {} as SkillUsageParams,
    actionProbabilities: {} as ActionProbabilityParams,
    cardPlacement: {} as CardPlacementParams,
    chargeStrategy: {} as ChargeStrategyParams,
    deepSeaStrategy: {} as DeepSeaStrategyParams,
    boardEvaluation: {} as EvaluationWeights,
    magicEvaluation: {} as MagicEvaluationConfig,
  };

  const genomeRecord = genome as unknown as Record<string, Record<string, number>>;

  for (const [category, params] of Object.entries(PARAM_RANGES)) {
    // paramsがオブジェクトの場合のみ処理
    if (typeof params === 'object' && params !== null) {
      for (const [param, range] of Object.entries(params)) {
        // rangeがmin/maxを持つオブジェクトの場合のみ処理
        if (typeof range === 'object' && range !== null && 'min' in range && 'max' in range) {
          const value = range.min + Math.random() * (range.max - range.min);
          if (!genomeRecord[category]) {
            genomeRecord[category] = {};
          }
          genomeRecord[category][param] = value;
        }
      }
    }
  }

  return genome;
}

/**
 * ゲノムをクローン
 */
export function cloneGenome(genome: AIGenome): AIGenome {
  return JSON.parse(JSON.stringify(genome));
}

/**
 * 値を範囲内にクランプ
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * ゲノムを正規化（範囲外の値を修正）
 */
export function normalizeGenome(genome: AIGenome): AIGenome {
  const normalized = cloneGenome(genome);
  const normalizedRecord = normalized as unknown as Record<string, Record<string, number>>;

  for (const [category, params] of Object.entries(PARAM_RANGES)) {
    // paramsがオブジェクトの場合のみ処理
    if (typeof params === 'object' && params !== null) {
      for (const [param, range] of Object.entries(params)) {
        // rangeがmin/maxを持つオブジェクトの場合のみ処理
        if (typeof range === 'object' && range !== null && 'min' in range && 'max' in range) {
          if (normalizedRecord[category] && normalizedRecord[category][param] !== undefined) {
            const currentValue = normalizedRecord[category][param];
            normalizedRecord[category][param] = clamp(currentValue, range.min, range.max);
          }
        }
      }
    }
  }

  return normalized;
}

/**
 * ゲノムを文字列にシリアライズ
 */
export function serializeGenome(genome: AIGenome): string {
  return JSON.stringify(genome);
}

/**
 * 文字列からゲノムをデシリアライズ
 */
export function deserializeGenome(json: string): AIGenome {
  return JSON.parse(json) as AIGenome;
}

/**
 * 2つのゲノムの差分を計算
 */
export function getGenomeDifference(a: AIGenome, b: AIGenome): number {
  let totalDiff = 0;
  let count = 0;

  const aRecord = a as unknown as Record<string, Record<string, number>>;
  const bRecord = b as unknown as Record<string, Record<string, number>>;

  for (const [category, params] of Object.entries(PARAM_RANGES)) {
    // paramsがオブジェクトの場合のみ処理
    if (typeof params === 'object' && params !== null) {
      for (const [param, range] of Object.entries(params)) {
        // rangeがmin/maxを持つオブジェクトの場合のみ処理
        if (typeof range === 'object' && range !== null && 'min' in range && 'max' in range) {
          if (aRecord[category] && bRecord[category] &&
              aRecord[category][param] !== undefined && bRecord[category][param] !== undefined) {
            const aVal = aRecord[category][param];
            const bVal = bRecord[category][param];
            const normalizedDiff = Math.abs(aVal - bVal) / (range.max - range.min);
            totalDiff += normalizedDiff;
            count++;
          }
        }
      }
    }
  }

  return count > 0 ? totalDiff / count : 0;
}
